import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  HomeIcon,
  BuildingStorefrontIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useInViewReveal } from "@/hooks/useInViewReveal";

interface ServeCard {
  icon: ReactNode;
  titleKey: string;
  descKey: string;
}

const cards: ServeCard[] = [
  {
    icon: <HomeIcon className="w-10 h-10 text-homeowners-headline transition-transform duration-[240ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] group-hover:scale-105" />,
    titleKey: "WhoWeServe.Card1Title",
    descKey: "WhoWeServe.Card1Desc",
  },
  {
    icon: <BuildingStorefrontIcon className="w-10 h-10 text-homeowners-headline transition-transform duration-[240ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] group-hover:scale-105" />,
    titleKey: "WhoWeServe.Card2Title",
    descKey: "WhoWeServe.Card2Desc",
  },
  {
    icon: <BuildingOfficeIcon className="w-10 h-10 text-homeowners-headline transition-transform duration-[240ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] group-hover:scale-105" />,
    titleKey: "WhoWeServe.Card3Title",
    descKey: "WhoWeServe.Card3Desc",
  },
  {
    icon: <ChartBarIcon className="w-10 h-10 text-homeowners-headline transition-transform duration-[240ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] group-hover:scale-105" />,
    titleKey: "WhoWeServe.Card4Title",
    descKey: "WhoWeServe.Card4Desc",
  },
];

export const WhoWeServeSection = () => {
  const [t] = useTranslation("homeowners");
  const { ref: sectionRef, hasRevealed } = useInViewReveal();
  const [activeTab, setActiveTab] = useState("WhoWeServe");

  const tabs = [
    { key: "WhoWeServe", href: "#who-we-serve" },
    { key: "WhoWeCanPay", href: "#who-we-can-pay" },
    { key: "ConciergeService", href: "#concierge-service" },
    { key: "PricingSavings", href: "#pricing-savings" },
    { key: "FrequentQA", href: "#frequent-qa" },
  ];

  return (
    <section
      id="who-we-serve"
      ref={sectionRef}
      className="w-full bg-[#f6f5f2] px-6 sm:px-12 lg:px-24 pt-16 lg:pt-24 pb-20 lg:pb-32"
    >
      {/* Section heading — reveal animation (#1) */}
      <h2
        className={classNames(
          "font-playfair font-semibold text-homeowners-headline text-center leading-snug text-[clamp(1.875rem,1rem+3.5vw,4rem)] transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {t("EverythingYouNeed.Title")}
      </h2>

      {/* Tab navigation — reveal with delay */}
      <div
        className={classNames(
          "mt-10 lg:mt-16 flex flex-wrap gap-4 lg:gap-9 transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] delay-[80ms]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {tabs.map((tab) => (
          <a
            key={tab.key}
            href={tab.href}
            onClick={() => setActiveTab(tab.key)}
            className={classNames(
              "flex-1 min-w-[140px] text-center px-6 lg:px-12 py-4 lg:py-5 rounded-xl text-[clamp(0.875rem,0.7rem+0.5vw,1.25rem)] font-normal transition-all duration-[180ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] cursor-pointer",
              {
                "bg-homeowners-purple text-white": activeTab === tab.key,
                "bg-white text-black hover:brightness-[0.97] hover:-translate-y-[2px] hover:shadow-md active:translate-y-0":
                  activeTab !== tab.key,
              }
            )}
          >
            {t(`Tabs.${tab.key}`)}
          </a>
        ))}
      </div>

      {/* Who We Serve content — reveal with delay */}
      <div
        className={classNames(
          "mt-16 lg:mt-32 transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] delay-[160ms]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        <h3 className="font-minion text-homeowners-headline text-center leading-snug text-[clamp(1.5rem,0.8rem+3vw,3.75rem)]">
          {t("WhoWeServe.Title")}
        </h3>

        {/* 2x2 Card grid — card elevation interaction (#5) */}
        <div className="mt-10 lg:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-9">
          {cards.map((card) => (
            <div
              key={card.titleKey}
              className="group bg-white rounded-[40px] flex items-center gap-8 lg:gap-12 px-6 lg:px-8 py-8 lg:py-10 transition-all duration-[240ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-[6px] hover:shadow-xl hover:border-homeowners-purple/20 border border-transparent cursor-default"
            >
              {/* Icon circle */}
              <div className="flex-shrink-0 w-16 h-16 lg:w-[76px] lg:h-[76px] rounded-full bg-[#efefef] flex items-center justify-center">
                {card.icon}
              </div>

              {/* Text content */}
              <div className="flex flex-col gap-3 lg:gap-5">
                <p className="font-montserrat font-semibold text-[#1a1a1a] text-[clamp(1rem,0.8rem+0.8vw,1.75rem)] leading-snug">
                  {t(card.titleKey)}
                </p>
                <p className="font-montserrat font-normal text-[#1a1a1a] text-[clamp(0.875rem,0.7rem+0.6vw,1.5rem)] leading-snug">
                  {t(card.descKey)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
